import { CardOrientation } from '@space-core/components/customCard/base/enum'
import ProgressCard from '@space-core/components/customCard/progress/desktop'
import ProgressCardMobile from '@space-core/components/customCard/progress/mobile'
import Grid from '@space-core/components/grid'
import { GridDisplay, GridOrientation } from '@space-core/components/grid/enum'
import { Actions, Entities } from '@space-core/datahub'
import { useMediaQuery } from '@space-core/hooks'
import useTranslation from '@space-core/hooks/useTranslation'
import { ScreenSizes } from '@space-core/styles'
import { PixelGroup, PixelPhase, SuperPixelUtils } from '@space-core/utils/superPixel'

import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'
import useMembership from '@space-shell/common/hooks/useMembership'
import type { Progress } from '@space-shell/common/services/product'
import { ProductUtils } from '@space-shell/common/utils/product'
import { useAppStore, useAuthStore } from '@space-shell/core/store'
import { useNavigate } from 'react-router-dom'

import styled from 'styled-components'

import { MAX_HORIZONTAL_CARDS, MAX_ITEMS_CAROUSEL } from './constants'
import type { ProgressListProps } from './types'

const CustomGrid = styled(Grid)`
  grid-row-gap: var(--spacer-7);
  grid-column-gap: var(--spacer-4);
`

const ProgressList = ({ data }: ProgressListProps) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { id: userId } = useAuthStore()
  const { membershipSlug, membershipData } = useMembership()
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.lg})`)
  const { sendDataHubEvent } = useDataHubEvents()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const { isPreview } = useAppStore()

  const handleClick = (product: Progress) => {
    if (isPreview) {
      return
    }
    const language = String(i18n.language).toLowerCase()
    const { id: productId, currentPage, pageType } = product

    sendDataHubEvent({
      group: Entities.HOME_KEEP_WATCHING,
      name: Actions.CLICK_PRODUCT,
      params: {
        product: {
          id: product.id,
          name: product.name
        }
      }
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_PRODUCT,
      label: `productId:${productId}`
    })

    SuperPixelUtils.sendEvent({
      ownerUcode: membershipData?.ownerUcode as string,
      instanceId: membershipSlug,
      group: PixelGroup.PreConsumptionEvents,
      phase: PixelPhase.KeepWatchingClick,
      runtimeValues: {
        slug: membershipSlug,
        userId: userId,
        productId: product.id,
        productName: product.name,
        mediaType: product.mediaType,
        progress: product.completedPercentage
      }
    })

    if (pageType) {
      navigate({
        pathname: `/${language}/club/${membershipSlug}/products/${productId}/${pageType.toLowerCase()}/${currentPage}`,
        search: `source=HOME_APP_CONTINUE_WATCHING`
      })

      return
    }

    navigate(`/${language}/club/${membershipSlug}/products/${productId}`)
  }

  const orientation =
    data.length > MAX_HORIZONTAL_CARDS ? CardOrientation.VERTICAL : CardOrientation.HORIZONTAL

  const isCarousel = data.length <= MAX_ITEMS_CAROUSEL && data.length > 1 ? true : false

  return (
    <div data-test='progress-list-container'>
      <CustomGrid
        className='_mt-5'
        cardOrientation={orientation}
        gridOrientation={GridOrientation.HORIZONTAL}
        type={isMobile ? GridDisplay.MOBILE : GridDisplay.DESKTOP}
        isCarousel={isCarousel}>
        {data.map((product) => {
          return isMobile ? (
            <ProgressCardMobile
              key={product.id}
              product={ProductUtils.progressMapping(product, t, isPreview)}
              handleClick={() => handleClick(product)}
              items={data.length}
              isCarousel={isCarousel}
            />
          ) : (
            <ProgressCard
              key={product.id}
              product={ProductUtils.progressMapping(product, t, isPreview)}
              handleClick={() => handleClick(product)}
              items={data.length}
              orientation={orientation}
            />
          )
        })}
      </CustomGrid>
    </div>
  )
}

export default ProgressList
